import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { reportPost } from '../api/socialActions';
import { useToast } from '../hooks/use-toast';

/**
 * Phase 50 — Report modal reachable from the three-dots menu on each post.
 *
 * Reason values map 1:1 to the backend `REPORT_REASONS` set. Keeping the
 * enum stable is critical because admin filters key off the same tokens.
 */
const REASONS = [
  { value: 'inappropriate', label: 'Contenuto inappropriato' },
  { value: 'sexual_adult', label: 'Contenuti sessuali / per adulti' },
  { value: 'minors_grooming', label: 'Coinvolge minori / grooming' },
  { value: 'spam', label: 'Spam' },
  { value: 'rules_violation', label: 'Violazione regole' },
  { value: 'offensive', label: 'Contenuto offensivo' },
  { value: 'copyright', label: 'Problema copyright' },
  { value: 'other', label: 'Altro' },
];

const ReportPostDialog = ({ open, onOpenChange, post }) => {
  const { toast } = useToast();
  const [reason, setReason] = useState('inappropriate');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const reset = () => {
    setReason('inappropriate');
    setNotes('');
    setSent(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const submit = async () => {
    if (!post?.id) return;
    setBusy(true);
    try {
      await reportPost(post.id, { reason, notes });
      setSent(true);
    } catch (err) {
      toast({
        title: 'Segnalazione non inviata',
        description: err?.response?.data?.detail || 'Riprova più tardi',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="report-post-dialog">
        {sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" strokeWidth={1.6} />
            <p className="text-sm">
              <span className="text-lg font-semibold">Grazie</span>, la segnalazione è stata inviata al team Nymia.
            </p>
            <Button className="mt-4" onClick={() => handleClose(false)} data-testid="report-post-close-btn">
              Chiudi
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" /> Segnala il post
              </DialogTitle>
              <DialogDescription>
                Aiutaci a mantenere Nymia una community sicura. La tua segnalazione è anonima
                verso l&apos;autore del post.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Motivo
                </p>
                <RadioGroup value={reason} onValueChange={setReason} className="gap-2">
                  {REASONS.map((r) => (
                    <div key={r.value} className="flex items-center gap-2">
                      <RadioGroupItem value={r.value} id={`report-reason-${r.value}`} data-testid={`report-reason-${r.value}`} />
                      <Label htmlFor={`report-reason-${r.value}`} className="cursor-pointer text-sm">
                        {r.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="report-notes" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Note (opzionali)
                </Label>
                <Textarea
                  id="report-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Aggiungi contesto se vuoi..."
                  maxLength={1000}
                  className="mt-1 h-24 resize-none bg-background"
                  data-testid="report-notes-input"
                />
                <p className="mt-1 text-right text-[10px] text-muted-foreground">
                  {notes.length}/1000
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => handleClose(false)}
                disabled={busy}
                data-testid="report-cancel-btn"
              >
                Annulla
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="gold-gradient"
                data-testid="report-submit-btn"
              >
                {busy ? 'Invio…' : 'Invia segnalazione'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportPostDialog;
